import React from "react";
import { ShieldAlert, RotateCcw, Home } from "lucide-react";

// Catches render crashes in a subtree and shows a recovery card instead of blanking the whole app.
// Pass resetKey (e.g. current view) — when it changes after a crash, the boundary resets automatically.
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="error-boundary-fallback">
        <div className="bg-white border border-[#FCE6E9] rounded-[2rem] max-w-sm w-full p-8 shadow-xl text-center">
          <div className="w-14 h-14 rounded-full bg-[#FCE6E9] flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="w-6 h-6 text-[#B04A6B]" />
          </div>
          <h2 className="text-base font-extrabold font-heading text-[#374151] mb-1.5" data-testid="error-boundary-title">
            This section hit a snag
          </h2>
          <p className="text-xs text-[#9CA3AF] font-medium mb-6">
            Something crashed while rendering this page. Your data is safe — try again or head back home.
          </p>
          <div className="flex gap-2">
            <button
              onClick={this.reset}
              data-testid="error-boundary-retry"
              className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 bg-[#374151] hover:bg-[#1F2937] text-white rounded-full text-xs font-extrabold transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Try Again
            </button>
            <button
              onClick={() => {
                this.reset();
                if (this.props.onHome) this.props.onHome();
                else window.location.assign("/");
              }}
              data-testid="error-boundary-home"
              className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 bg-white hover:bg-[#E6F2FA] border border-[#B3E0F2] text-[#3D7E9E] rounded-full text-xs font-extrabold transition-colors"
            >
              <Home className="w-3.5 h-3.5" /> Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
